import { List, ListItem, Stack } from "@mui/material";
import { readEnv } from "@repo/helpers";
import { type PensionContractType, LocalStorageKeys } from "@repo/logic";
import { BodyTextM, cs, HeadingS } from "@repo/ui";
import { type FC } from "react";
import { v4 as uuidv4 } from "uuid";
import { type AMLRedirect, type AMLScenario } from ".";
import ChangeRequestError from "../Drawer/GlobalSteps/ChangeRequestError";
import { type MultiStepChildren } from "../Drawer/MultiStepDrawer";
import SimpleDrawer from "../Drawer/SimpleDrawer";
import { TriggerButton } from "../Link";
import { type TriggerButtonProps } from "../Link/TriggerButton";

export const getBankIdAuthorizationUri = (): string => {
    return `${readEnv("GATSBY_BANK_ID_BASE_URL")!}/auth?client_id=${readEnv("GATSBY_BANK_ID_CLIENT_ID")!}&redirect_uri=${encodeURIComponent(
        `${window.location.origin}/bank-id/callback`,
    )}&scope=${encodeURIComponent(
        "profile.birthnumber profile.verification profile.legalstatus openid profile.gender profile.addresses profile.birthdate profile.name profile.birthplaceNationality profile.idcards",
    )}&response_type=token&state=BankID${uuidv4()}&nonce=${uuidv4()}&prompt=login&display=page&acr_values=loa3`;
};

export type AMLActionInfo = {
    redirect: AMLRedirect;
    contractId: string;
    contractType: PensionContractType;
};

type BankIdProps = Partial<TriggerButtonProps> & {
    actionInfo: AMLActionInfo;
};

export const BankIDAmlButton: FC<BankIdProps> = ({ onClick, actionInfo, ...rest }) => {
    return (
        <TriggerButton
            href={getBankIdAuthorizationUri()}
            variant="bank-id-no-line"
            fullWidth
            onClick={() => {
                localStorage.setItem(LocalStorageKeys.BANK_ID_LAST_ACTION, JSON.stringify(actionInfo));
                onClick?.();
            }}
            {...rest}
        >
            {cs.AML.fillOutAml}
        </TriggerButton>
    );
};

type IBankIdContent = {
    scenario: AMLScenario;
    showOfflineInstructions?: boolean;
};

export const BankIdIdentificationDrawerContent = ({ scenario, showOfflineInstructions = false }: IBankIdContent) => {
    return (
        <Stack direction="column" gap={4}>
            <HeadingS marginBottom={0}>{cs.AML.amlReasoning.greeting}</HeadingS>
            <BodyTextM>
                {scenario === "contribution"
                    ? cs.AML.amlReasoning.monthlyContribution
                    : cs.AML.amlReasoning.insurancePayout}
            </BodyTextM>
            <BodyTextM strong>{cs.AML.amlInstructions.online}</BodyTextM>
            {showOfflineInstructions && (
                <>
                    <BodyTextM>{cs.AML.amlInstructions.offline.physicalIdentification}</BodyTextM>
                    <BodyTextM>1. {cs.AML.amlInstructions.offline.nnLocation}</BodyTextM>
                    <Stack>
                        <BodyTextM>2. {cs.AML.amlInstructions.offline.paidLocations.label}</BodyTextM>
                        <List sx={{ pl: 4, listStyleType: "disc" }}>
                            <ListItem sx={{ display: "list-item", pl: 0 }}>
                                {cs.AML.amlInstructions.offline.paidLocations.govermentOffice}
                            </ListItem>
                            <ListItem sx={{ display: "list-item", pl: 0 }}>
                                {cs.AML.amlInstructions.offline.paidLocations.notarius}
                            </ListItem>
                            <ListItem sx={{ display: "list-item", pl: 0 }}>
                                {cs.AML.amlInstructions.offline.paidLocations.czechPoint}
                            </ListItem>
                        </List>
                        <BodyTextM>{cs.AML.amlInstructions.offline.sendIdList}</BodyTextM>
                    </Stack>
                </>
            )}
        </Stack>
    );
};

type IBankIdIdentificationDrawer = {
    action: AMLActionInfo;
    open?: boolean;
    onClick?: () => void;
    onCloseCallback?: () => void;
    disabled?: boolean;
} & IBankIdContent;

export const BankIdIdentification = ({
    onCloseCallback,
    action,
    onClick,
    open = false,
    ...rest
}: IBankIdIdentificationDrawer) => {
    return (
        <SimpleDrawer
            title={cs.AML.fillOutAml}
            open={open}
            isCloseIcon
            customButton={<BankIDAmlButton onClick={onClick} actionInfo={action} />}
            onClose={() => {
                onCloseCallback && onCloseCallback();
            }}
        >
            <BankIdIdentificationDrawerContent {...rest} />
        </SimpleDrawer>
    );
};

type IGetBankIdIndetificationDrawerStep = {
    actionInfo: AMLActionInfo;
    scenario: AMLScenario;
    onSkip: () => void;
};

export const GetBankIdIndetificationDrawerStep = ({
    actionInfo,
    scenario,
    onSkip,
}: IGetBankIdIndetificationDrawerStep): MultiStepChildren => {
    return {
        component: <BankIdIdentificationDrawerContent scenario={scenario} />,
        stepName: "AMLIdentification",
        stepConfig: {
            customNextButton: (
                <Stack direction="row" gap={4}>
                    <BankIDAmlButton actionInfo={actionInfo} />
                    <TriggerButton variant="outlined" onClick={onSkip}>
                        Přeskočit
                    </TriggerButton>
                </Stack>
            ),
        },
    };
};

type IGetBankIdIndetificationDrawerErrorStep = {
    errorMessage?: string;
    onSkip: () => void;
};

export const GetBankIdIndetificationDrawerErrorStep = ({
    errorMessage,
    onSkip,
}: IGetBankIdIndetificationDrawerErrorStep): MultiStepChildren => {
    return {
        stepName: "BankIdError",
        component: (
            <ChangeRequestError description={errorMessage ?? cs.AML.bankIdErrorMessages.bankIdValidationError} />
        ),
        stepConfig: {
            customFunction() {
                onSkip();
            },
        },
    };
};
